import React from "react";
import { Link } from "react-router-dom";

import { AlertTriangle, Database } from "@/components/icons";
import { HttpError } from "@/lib/http";
import { telemetryErrorCode, telemetryErrorDetail, telemetryErrorMessage } from "@/lib/telemetry";
import { LakeConfigMismatch } from "@/components/telemetry/LakeFreshness";
import { formatBytes } from "@/components/telemetry/format";

/** Codes that mean "your request", not "the lake" — they belong next to the controls. */
const REQUEST_CODES = new Set([
  "invalid_range",
  "range_too_wide",
  "window_too_wide",
  "granularity_required",
  "limit_too_large",
  "unknown_metric",
  "bucket_too_small",
  "invalid_cursor",
  "invalid_unit_id",
  "raw_not_allowed_for_range",
  "too_many_refs",
  "admin_override_required",
  "query_cancelled_by_cutoff",
]);

/**
 * Every code here tells support what to do next — narrow the range, wait for
 * the feeder, ask for an override. A bare "failed to load" tells them nothing,
 * and a white screen on a support tool during an incident is its own outage.
 */
export function TelemetryErrorPanel({ error, unitId }: { error: unknown; unitId: string }) {
  const code = telemetryErrorCode(error);
  const message = telemetryErrorMessage(error);
  const detail = telemetryErrorDetail(error);
  const status = error instanceof HttpError ? error.status : 0;

  if (code === "lake_config_mismatch") {
    return <LakeConfigMismatch detail={detail} />;
  }

  if (code === "lake_disabled") {
    return (
      <Panel
        testId="telemetry-lake-disabled"
        tone="muted"
        icon={<Database className="h-4 w-4" />}
        title="Telemetry lake is disabled on this installation"
      >
        <p>{message}</p>
        <LogsLink unitId={unitId} />
      </Panel>
    );
  }

  if (code === "lake_unavailable") {
    return (
      <Panel
        testId="telemetry-lake-unavailable"
        tone="warn"
        icon={<AlertTriangle className="h-4 w-4" />}
        title="Telemetry lake unavailable"
      >
        <p>{message}</p>
        <LogsLink unitId={unitId} />
      </Panel>
    );
  }

  if (code === "budget_exceeded") {
    const spent = Number(detail.spent_bytes_today ?? 0);
    const limit = Number(detail.limit_bytes ?? 0);
    return (
      <Panel
        testId="telemetry-budget-exceeded"
        tone="warn"
        icon={<AlertTriangle className="h-4 w-4" />}
        title="Daily query budget reached"
      >
        <p>{message}</p>
        <p className="opacity-70">
          Scanned {formatBytes(spent)} of {formatBytes(limit)} ({String(detail.scope ?? "user")} scope).
          A platform administrator can raise the limit.
        </p>
      </Panel>
    );
  }

  if (code === "rate_limited" || code === "too_many_queries") {
    return (
      <Panel
        testId="telemetry-rate-limited"
        tone="warn"
        icon={<AlertTriangle className="h-4 w-4" />}
        title="Too many telemetry queries"
      >
        <p>{message} Wait a moment and refresh.</p>
      </Panel>
    );
  }

  if (code && REQUEST_CODES.has(code)) {
    return (
      <Panel
        testId="telemetry-request-error"
        tone="warn"
        icon={<AlertTriangle className="h-4 w-4" />}
        title={code === "query_cancelled_by_cutoff" ? "Query exceeded the scan limit" : "Request not allowed"}
      >
        <p>{message}</p>
        <p className="opacity-70">Narrow the range or reduce the filters, then refresh.</p>
      </Panel>
    );
  }

  if (status === 403) {
    return (
      <Panel
        testId="telemetry-forbidden"
        tone="warn"
        icon={<AlertTriangle className="h-4 w-4" />}
        title="Forbidden"
      >
        <p>{message || "You do not have access to this charger's telemetry."}</p>
      </Panel>
    );
  }

  return (
    <Panel
      testId="telemetry-error"
      tone="warn"
      icon={<AlertTriangle className="h-4 w-4" />}
      title="Telemetry query failed"
    >
      <p>{message}</p>
      <LogsLink unitId={unitId} />
    </Panel>
  );
}

function LogsLink({ unitId }: { unitId: string }) {
  return (
    <Link
      to={`/logs?unit_id=${encodeURIComponent(unitId)}`}
      className="mt-1 inline-block text-primary hover:underline"
    >
      Open live logs (Redis) for this charger →
    </Link>
  );
}

function Panel({
  title,
  children,
  icon,
  tone,
  testId,
}: {
  title: string;
  children: React.ReactNode;
  icon: React.ReactNode;
  tone: "muted" | "warn";
  testId: string;
}) {
  const toneClass =
    tone === "warn"
      ? "border-amber-400/60 bg-amber-400/10 text-amber-900 dark:text-amber-200"
      : "border-border bg-muted/30";
  return (
    <div data-testid={testId} className={`rounded-lg border p-4 text-sm ${toneClass}`}>
      <div className="flex items-center gap-2 font-medium">
        {icon}
        {title}
      </div>
      <div className="mt-1 space-y-1">{children}</div>
    </div>
  );
}
